import { Menu, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { trackEvent } from "../../lib/analytics";
import { productUrl } from "../../lib/appUrls";
import styles from "./NavHeader.module.css";

type NavItem = {
  to: string;
  label: string;
};

type NavHeaderProps = {
  items?: NavItem[];
  variant?: "light" | "dark";
};

const defaultItems: NavItem[] = [
  { to: "/production-scheduling-software", label: "nav.public.product" },
  { to: "/industries", label: "nav.public.industries" },
  { to: "/pricing", label: "nav.public.pricing" },
  { to: "/contact", label: "nav.public.contact" },
];

export default function NavHeader({ items = defaultItems, variant = "light" }: NavHeaderProps) {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMenu = () => setMobileMenuOpen(false);
  const handleTrialClick = () => {
    closeMenu();
    trackEvent("trial_cta_clicked", { placement: "navigation" });
  };

  return (
    <header className={`${styles.shell} ${variant === "dark" ? styles.dark : ""}`}>
      <div className={styles.nav}>
        <NavLink to="/" className={styles.logo} onClick={closeMenu}>
          <span className={styles.logoMark} aria-hidden="true">
            <img src="/runmark-shift-counterform.svg" alt="" width={28} height={28} />
          </span>
          <span className={styles.logoText}>{t("common.brand")}</span>
        </NavLink>

        <nav className={styles.links} aria-label={t("nav.public.primaryAria")}>
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `${styles.link} ${isActive ? styles.active : ""}`
                }
              >
                {t(item.label)}
              </NavLink>
            ))}
        </nav>

        <div className={styles.cta}>
          <a href={productUrl("/login")} className={styles.loginLink} onClick={closeMenu}>
            {t("nav.actions.signIn")}
          </a>
          <a href={productUrl("/register")} className={styles.primaryButton} onClick={handleTrialClick}>
            {t("nav.actions.getStarted")}
          </a>
        </div>

        <button
            type="button"
            className={styles.mobileMenuButton}
            aria-label={
              mobileMenuOpen ? t("nav.public.closeMenu") : t("nav.public.openMenu")
            }
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
          >
            {mobileMenuOpen ? <X className={styles.mobileMenuIcon} /> : <Menu className={styles.mobileMenuIcon} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className={styles.mobilePanel}>
          <nav className={styles.mobileLinks} aria-label={t("nav.public.mobilePrimaryAria")}>
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.active : ""}`}
                onClick={closeMenu}
              >
                {t(item.label)}
              </NavLink>
            ))}
          </nav>
          <div className={styles.mobileActions}>
            <a href={productUrl("/login")} className={styles.loginLink} onClick={closeMenu}>
              {t("nav.actions.signIn")}
            </a>
            <a href={productUrl("/register")} className={styles.primaryButton} onClick={handleTrialClick}>
              {t("nav.actions.getStarted")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
